import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useMemo, useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import LicenseUpsell from "@/components/LicenseUpsell";
import LessonBlocks from "@/components/LessonBlocks";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ArrowRight, CheckCircle2, Trophy, Bookmark, StickyNote, Save, MessageSquareText } from "lucide-react";

export default function LessonView() {
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);

  const modQ = useQuery({ queryKey: ["module", moduleId], queryFn: async () => (await api.get(`/course/${moduleId}`)).data, retry: false });
  const progQ = useQuery({ queryKey: ["progress"], queryFn: async () => (await api.get("/progress")).data });

  const m = modQ.data;
  const { lesson, index } = useMemo(() => {
    if (!m) return { lesson: null, index: -1 };
    const idx = m.lessons.findIndex((l) => l.id === lessonId);
    return { lesson: m.lessons[idx], index: idx };
  }, [m, lessonId]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [lessonId]);

  const metaQ = useQuery({ queryKey: ["lesson-meta"], queryFn: async () => (await api.get("/lesson-meta")).data });
  const bookmarked = !!metaQ.data?.meta?.[lessonId]?.bookmarked;
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    setNote(metaQ.data?.meta?.[lessonId]?.note || "");
  }, [lessonId, metaQ.data]);

  useEffect(() => {
    api.post("/progress/view", { module_id: moduleId, lesson_id: lessonId }).catch(() => {});
  }, [moduleId, lessonId]);

  const saveMeta = async (patch) => {
    await api.put(`/lesson-meta/${moduleId}/${lessonId}`, patch);
    await qc.invalidateQueries({ queryKey: ["lesson-meta"] });
  };

  const toggleBookmark = async () => {
    const next = !bookmarked;
    try {
      await saveMeta({ bookmarked: next });
      toast.success(next ? "Lesezeichen gesetzt" : "Lesezeichen entfernt");
    } catch {
      toast.error("Konnte nicht gespeichert werden.");
    }
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await saveMeta({ note });
      toast.success("Notiz gespeichert");
    } catch {
      toast.error("Notiz konnte nicht gespeichert werden.");
    } finally {
      setSavingNote(false);
    }
  };

  if (modQ.error?.response?.status === 403) {
    return (
      <div className="min-h-screen bg-background grain">
        <Navbar />
        <main className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
          <Link to={`/module/${moduleId}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" /> Zurück zum Kurs
          </Link>
          <LicenseUpsell />
        </main>
      </div>
    );
  }

  if (modQ.isLoading || !m || !lesson) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-10 space-y-4">
          <div className="h-8 w-1/2 rounded glass animate-pulse" />
          <div className="h-64 rounded-2xl glass animate-pulse" />
        </div>
      </div>
    );
  }

  const done = new Set(progQ.data?.modules?.[moduleId]?.completed_lesson_ids || []);
  const isDone = done.has(lessonId);
  const isLast = index === m.lessons.length - 1;
  const nextLesson = m.lessons[index + 1];
  const prevLesson = m.lessons[index - 1];

  const markComplete = async () => {
    setSaving(true);
    try {
      await api.post("/progress/lesson", { module_id: moduleId, lesson_id: lessonId });
      await qc.invalidateQueries({ queryKey: ["progress"] });
      toast.success("Lektion abgeschlossen!");
      if (nextLesson) navigate(`/module/${moduleId}/lesson/${nextLesson.id}`);
      else navigate(`/module/${moduleId}/quiz`);
    } catch (err) {
      toast.error("Konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const progressPct = Math.round(((index + 1) / m.lessons.length) * 100);

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      {/* sticky progress bar */}
      <div className="sticky top-16 z-40 h-1 bg-white/5">
        <div className="h-full bg-gradient-to-r from-amber-500 to-gold transition-all duration-500" style={{ width: `${progressPct}%`, background: "linear-gradient(90deg,#F5A623,#D4AF37)" }} />
      </div>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
        <Link to={`/module/${moduleId}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> {m.title}
        </Link>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-gold">Lektion {index + 1}/{m.lessons.length}</span>
              {isDone && <span className="flex items-center gap-1 text-xs text-gold"><CheckCircle2 className="h-3.5 w-3.5" /> abgeschlossen</span>}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/coach", { state: { moduleId, lessonId, moduleNumber: m.number, lessonTitle: lesson.title } })}
                data-testid="ask-coach-lesson"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-xs text-muted-foreground hover:text-gold transition-all duration-300"
              >
                <MessageSquareText className="h-3.5 w-3.5" /> Coach fragen
              </button>
              <button
                onClick={toggleBookmark}
                data-testid="bookmark-toggle"
                aria-pressed={bookmarked}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all duration-300 ${bookmarked ? "bg-gold/15 border border-gold/40 text-gold" : "glass text-muted-foreground hover:text-gold"}`}
              >
                <Bookmark className={`h-3.5 w-3.5 ${bookmarked ? "fill-gold" : ""}`} /> {bookmarked ? "Gemerkt" : "Merken"}
              </button>
            </div>
          </div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mb-6">{lesson.title}</h1>
        </motion.div>

        <article>
          <LessonBlocks blocks={lesson.blocks} />
        </article>

        {/* Persönliche Notiz */}
        <section data-testid="lesson-note" className="mt-10 rounded-2xl glass p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <StickyNote className="h-5 w-5 text-gold" />
            <h3 className="font-heading font-bold text-lg">Ihre Notiz</h3>
          </div>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="lesson-note-input"
            placeholder="Halten Sie hier Ihre Gedanken, Fragen oder Merksätze zu dieser Lektion fest …"
            className="min-h-28 bg-white/5 border-white/10 focus-visible:ring-gold/40"
          />
          <div className="flex justify-end mt-3">
            <button
              onClick={saveNote}
              disabled={savingNote}
              data-testid="lesson-note-save"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass text-sm hover:glow-gold transition-all duration-300 disabled:opacity-60"
            >
              <Save className="h-4 w-4 text-gold" /> {savingNote ? "Speichern…" : "Notiz speichern"}
            </button>
          </div>
        </section>

        {/* Footer nav */}
        <div className="mt-12 pt-8 border-t hairline flex items-center justify-between gap-4">
          {prevLesson ? (
            <Link
              to={`/module/${moduleId}/lesson/${prevLesson.id}`}
              data-testid="prev-lesson-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass text-sm hover:glow-gold transition-all"
            >
              <ArrowLeft className="h-4 w-4" /> Zurück
            </Link>
          ) : (
            <Link
              to={`/module/${moduleId}`}
              data-testid="back-to-module-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full glass text-sm hover:glow-gold transition-all"
            >
              <ArrowLeft className="h-4 w-4" /> Übersicht
            </Link>
          )}

          <button
            onClick={markComplete}
            disabled={saving}
            data-testid="complete-lesson-button"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-60"
          >
            {isLast ? (
              <>Abschliessen & zum Quiz <Trophy className="h-4 w-4" /></>
            ) : (
              <>{isDone ? "Weiter" : "Abgeschlossen & weiter"} <ArrowRight className="h-4 w-4" /></>
            )}
          </button>
        </div>
      </main>
    </div>
  );
}
